import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import './editor.css';
import './number-input.css';
import { sanitizeHtml } from '../utils/sanitize';
import { validateCampaignNotes } from '../utils/validation';

interface Campaign {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  type: 'promotion' | 'content' | 'rcf_planner';
  notes: string;
  rcf_data?: {
    promotionDays: number;
    contentDays: number;
  };
}

interface CampaignFormProps {
  campaign?: Campaign;
  selectedDate: Date | null;
  onClose: () => void;
  onSubmit: (campaign: Omit<Campaign, 'id'>) => void;
  existingCampaigns: Campaign[];
  onDelete?: (campaignId: string) => void;
  timezone?: string;
}

export const CampaignForm: React.FC<CampaignFormProps> = ({
  campaign,
  selectedDate,
  onClose,
  onSubmit,
  existingCampaigns,
  onDelete,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone // Default to browser timezone
}) => {
  const [formData, setFormData] = useState<Omit<Campaign, 'id'>>({
    name: campaign?.name || '',
    type: campaign?.type || 'promotion',
    startDate: campaign?.startDate ? new Date(campaign.startDate) : (selectedDate ? new Date(selectedDate) : new Date()),
    endDate: campaign?.endDate ? new Date(campaign.endDate) : new Date(),
    notes: campaign?.notes || '',
    rcf_data: campaign?.rcf_data || { contentDays: 2, promotionDays: 3 }
  });
  const [duration, setDuration] = useState(1);
  const [contentDays, setContentDays] = useState(Math.max(1, campaign?.rcf_data?.contentDays || 2));
  const [promotionDays, setPromotionDays] = useState(Math.max(1, campaign?.rcf_data?.promotionDays || 3));
  const [error, setError] = useState<string | null>(null);
  const [markedForDeletion, setMarkedForDeletion] = useState(false);
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // State for custom link input
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  
  // State for editing notes
  const [isEditing, setIsEditing] = useState(false);
  const [savedNotes, setSavedNotes] = useState('');
  
  // Ref to track processed timezone changes to prevent double processing
  const didProcessTimezoneChange = useRef(false);
  
  // Update form data when notes content changes
  const handleNotesChange = (content: string) => {
    // Sanitize the HTML content to prevent XSS
    const sanitizedContent = sanitizeHtml(content);
    
    setFormData(prev => ({
      ...prev,
      notes: sanitizedContent
    }));
  };

  // Configure the editor
  const inlineEditor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {},
        orderedList: {},
      }),
      Link.configure({
        openOnClick: !isEditing,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Underline,
    ],
    content: formData.notes || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none w-full min-h-[150px]',
      },
    },
    onUpdate: ({ editor }) => {
      handleNotesChange(editor.getHTML());
    },
    editable: isEditing,
    autofocus: false,
  });

  const expandedEditor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {},
        orderedList: {},
      }),
      Link.configure({
        openOnClick: !isEditing,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Underline,
    ],
    content: formData.notes || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none w-full min-h-[300px]',
      },
    },
    onUpdate: ({ editor }) => {
      handleNotesChange(editor.getHTML());
    },
    editable: isEditing,
    autofocus: false,
  });
  
  // Update editor editable state when isEditing changes
  useEffect(() => {
    const updateEditableState = (editor: typeof inlineEditor | typeof expandedEditor) => {
      if (!editor) return;
      
      // Set editable status
      if (editor.isEditable !== isEditing) {
        editor.setEditable(isEditing);
        console.log(`Set ${editor === inlineEditor ? 'inline' : 'expanded'} editor editable to: ${isEditing}`);
      }
    };

    updateEditableState(inlineEditor);
    updateEditableState(expandedEditor);
    
  }, [isEditing, inlineEditor, expandedEditor]);

  // Update editor content when formData.notes changes
  useEffect(() => {
    if (formData.notes !== inlineEditor?.getHTML()) {
      inlineEditor?.commands.setContent(formData.notes);
    }
    if (formData.notes !== expandedEditor?.getHTML()) {
      expandedEditor?.commands.setContent(formData.notes);
    }
  }, [formData.notes, inlineEditor, expandedEditor]);

  // Sync editors when switching between inline and expanded views
  useEffect(() => {
    const activeEditor = isNotesExpanded ? expandedEditor : inlineEditor;
    const inactiveEditor = isNotesExpanded ? inlineEditor : expandedEditor;
    
    if (activeEditor && inactiveEditor) {
      // Get content from the active editor
      const content = activeEditor.getHTML();
      
      // Update the inactive editor with this content
      inactiveEditor.commands.setContent(content);
      
      // Focus the active editor when switching views
      if (isNotesExpanded) {
        setTimeout(() => {
          expandedEditor?.commands.focus();
        }, 100);
      } else {
        setTimeout(() => {
          inlineEditor?.commands.focus();
        }, 100);
      }
    }
  }, [isNotesExpanded, inlineEditor, expandedEditor]);
  
  // Set initial content when campaign changes
  useEffect(() => {
    if (campaign) {
      // Ensure we create new Date objects to preserve local timezone
      const campaignStartDate = new Date(campaign.startDate);
      const campaignEndDate = new Date(campaign.endDate);
      
      // Make sure rcf_data has valid values (at least 1 for each)
      const safeRcfData = campaign.rcf_data || { contentDays: 2, promotionDays: 3 };
      safeRcfData.contentDays = Math.max(1, safeRcfData.contentDays);
      safeRcfData.promotionDays = Math.max(1, safeRcfData.promotionDays);
      
      const initialFormData = {
        name: campaign.name,
        startDate: campaignStartDate,
        endDate: campaignEndDate,
        type: campaign.type,
        notes: campaign.notes || '',
        rcf_data: safeRcfData
      };
      setFormData(initialFormData);
      
      // Set individual day counts for RCF planner
      if (campaign.type === 'rcf_planner' && campaign.rcf_data) {
        setContentDays(Math.max(1, campaign.rcf_data.contentDays));
        setPromotionDays(Math.max(1, campaign.rcf_data.promotionDays));
      }
      
      // Calculate duration while preserving local timezone
      // Normalize time to start of day for more consistent calculations
      const normalizedStart = new Date(campaignStartDate);
      normalizedStart.setHours(0, 0, 0, 0);
      const normalizedEnd = new Date(campaignEndDate);
      normalizedEnd.setHours(0, 0, 0, 0);
      
      // Add 1 day because duration includes both start and end days
      const diffTime = Math.abs(normalizedEnd.getTime() - normalizedStart.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      
      console.log(`[CampaignForm] Calculated duration: ${diffDays} days from ${normalizedStart.toISOString()} to ${normalizedEnd.toISOString()}`);
      setDuration(diffDays);
      
      // Set editor content after a brief delay to ensure editors are initialized
      setTimeout(() => {
        inlineEditor?.commands.setContent(initialFormData.notes);
        expandedEditor?.commands.setContent(initialFormData.notes);
      }, 0);
      
    } else {
      // Reset form for new campaign
      const defaultStartDate = selectedDate ? new Date(selectedDate) : new Date();
      defaultStartDate.setHours(0, 0, 0, 0); // Normalize time to start of day
      
      const initialFormData = {
        name: '',
        type: 'promotion' as 'promotion' | 'content' | 'rcf_planner',
        startDate: defaultStartDate,
        endDate: new Date(defaultStartDate), // Create a new date to avoid reference issues
        notes: '',
        rcf_data: { contentDays: 2, promotionDays: 3 }
      };
      setFormData(initialFormData);
      setDuration(1);
      setContentDays(1);
      setPromotionDays(1);
      
      // Clear editor content
      setTimeout(() => {
        inlineEditor?.commands.setContent('');
        expandedEditor?.commands.setContent('');
      }, 0);
    }
  }, [campaign, selectedDate]);

  // Update form data when timezone changes
  useEffect(() => {
    console.log('[TIMEZONE] Changed to:', timezone);
    
    // Skip update if we're still in the initial render cycle
    if (!timezone) {
      console.log('[TIMEZONE] No timezone provided, skipping update');
      return;
    }
    
    // Only process this once per timezone change
    if (didProcessTimezoneChange.current) {
      return;
    }
    
    didProcessTimezoneChange.current = true;
    
    if (campaign) {
      // Re-apply the campaign dates with the new timezone context
      const campaignStartDate = new Date(campaign.startDate);
      const campaignEndDate = new Date(campaign.endDate);
      
      console.log('[TIMEZONE] Updating dates for timezone:', {
        timezone,
        oldStartDate: formData.startDate,
        oldEndDate: formData.endDate,
        newStartDate: campaignStartDate,
        newEndDate: campaignEndDate
      });
      
      setFormData(prev => ({
        ...prev,
        startDate: campaignStartDate,
        endDate: campaignEndDate
      }));
      
      // Recalculate duration
      const diffTime = Math.abs(campaignEndDate.getTime() - campaignStartDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setDuration(diffDays);
    } else if (selectedDate) {
      // For new campaigns, update with the selected date in the new timezone
      setFormData(prev => ({
        ...prev,
        startDate: new Date(selectedDate)
      }));
    }
    
    // Reset flag when timezone changes
    return () => {
      didProcessTimezoneChange.current = false;
    };
  }, [timezone, campaign, selectedDate]);

  const checkOverlap = (newStartDate: Date, newEndDate: Date, newType: 'promotion' | 'content' | 'rcf_planner') => {
    // When editing an existing campaign, make sure we have the campaign ID for proper comparison
    const currentCampaignId = campaign?.id;
    
    return existingCampaigns.some(existingCampaign => {
      // Skip the current campaign when editing
      if (currentCampaignId && existingCampaign.id === currentCampaignId) {
        return false;
      }
      
      // Make sure dates are properly comparable by normalizing them in the local time zone
      const existingStart = new Date(existingCampaign.startDate);
      existingStart.setHours(0, 0, 0, 0);
      
      const existingEnd = new Date(existingCampaign.endDate);
      existingEnd.setHours(0, 0, 0, 0);
      
      const newStart = new Date(newStartDate);
      newStart.setHours(0, 0, 0, 0);
      
      const newEnd = new Date(newEndDate);
      newEnd.setHours(0, 0, 0, 0);
      
      // Check if the new campaign overlaps with existing campaign
      const hasDateOverlap = (
        (newStart <= existingEnd && newEnd >= existingStart)
      );

      // All campaigns should detect date overlaps
      return hasDateOverlap;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (markedForDeletion && onDelete) {
      onDelete(campaign!.id);
      onClose();
      return;
    }
    
    if (!formData.name.trim()) {
      setError('Campaign name is required');
      return;
    }

    // Validate campaign notes
    const notesValidation = validateCampaignNotes(formData.notes);
    if (!notesValidation.isValid) {
      setError(notesValidation.message);
      return;
    }

    try {
      // For RCF planner campaigns, ensure we have at least 1 day for promotion
      if (formData.type === 'rcf_planner') {
        if (!formData.rcf_data || !formData.rcf_data.promotionDays) {
          setError('RCF campaigns must have at least 1 day for promotion');
          return;
        }
        
        // NOTE: We now allow 0 content days for RCF planner campaigns
        // to be consistent with the dedicated RCF planner page
        
        if (formData.rcf_data.promotionDays < 1) {
          setError('Promotion days must be at least 1');
          return;
        }
      }
      
      // Calculate end date for normal campaigns
      let endDate = formData.endDate;
      if (formData.type !== 'rcf_planner') {
        // For non-RCF campaigns, calculate end date based on duration
        endDate = new Date(formData.startDate);
        endDate.setDate(endDate.getDate() + (duration - 1));
      } else {
        // For RCF campaigns, calculate end date based on content and promotion days
        const totalDays = contentDays + promotionDays;
        endDate = new Date(formData.startDate);
        endDate.setDate(endDate.getDate() + (totalDays - 1));
      }
      
      // Check for campaign overlaps
      if (checkOverlap(formData.startDate, endDate, formData.type)) {
        setError('Campaign overlaps with an existing campaign');
        return;
      }
      
      console.log('Submitting campaign:', { ...formData, endDate });
      
      // Sanitize notes before submission
      const sanitizedNotes = sanitizeHtml(formData.notes);
      
      // If this is an rcf_planner campaign, make sure rcf_data is included
      if (formData.type === 'rcf_planner') {
        const updatedFormData = {
          ...formData,
          notes: sanitizedNotes,
          endDate,
          rcf_data: {
            // Allow content days to be 0 if that's what was set
            contentDays: contentDays,
            promotionDays: Math.max(1, promotionDays)
          }
        };
        onSubmit(updatedFormData);
      } else {
        // For non-rcf campaigns, remove any rcf_data
        const { rcf_data, ...nonRcfFormData } = formData;
        onSubmit({
          ...nonRcfFormData,
          notes: sanitizedNotes,
          endDate
        });
      }
      
      onClose();
    } catch (error) {
      setError('Error creating campaign');
      console.error(error);
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDuration = parseInt(e.target.value, 10) || 1;
    
    if (newDuration < 1) {
      return; // Ignore invalid input
    }
    
    setDuration(newDuration);
    
    // Create a clean copy of the start date and normalize it
    const startDate = new Date(formData.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    // Update end date based on start date and duration
    const newEndDate = new Date(startDate);
    newEndDate.setDate(startDate.getDate() + (newDuration - 1));
    
    console.log(`[CampaignForm] Duration changed to ${newDuration} days. New end date: ${newEndDate.toISOString()}`);
    
    setFormData(prev => ({ 
      ...prev, 
      endDate: newEndDate 
    }));
  };

  // New handler to ensure value is applied when spinner is clicked
  const handleSpinnerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // The main purpose of this function is to prevent text selection
    // while still allowing spinner clicks
    e.preventDefault();
    
    // Ensure focus remains on the input
    const target = e.target as HTMLInputElement;
    target.focus();
    
    // Get the current value directly from the DOM element to ensure it's up to date
    // This handles the case where the user clicks the spinner buttons
    const newDuration = parseInt(target.value, 10) || 1;
    
    // Only update if the value is different from our current state
    if (newDuration !== duration) {
      setDuration(newDuration);
      
      // Create a clean copy of the start date and normalize it
      const startDate = new Date(formData.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      // Update end date based on start date and duration
      const newEndDate = new Date(startDate);
      newEndDate.setDate(startDate.getDate() + (newDuration - 1));
      
      console.log(`[CampaignForm] Duration updated from spinner click to ${newDuration} days. New end date: ${newEndDate.toISOString()}`);
      
      setFormData(prev => ({
        ...prev,
        endDate: newEndDate
      }));
    }
    
    // Let the native spinner buttons work
    console.log('Spinner clicked on input:', target.id);
  };

  // Add blur handler to ensure value is applied when clicking away
  const handleDurationBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Get the current value from the input
    const newDuration = parseInt(e.target.value, 10) || 1;
    
    // Update state if different from current duration
    if (newDuration !== duration) {
      setDuration(newDuration);
      
      // Create a clean copy of the start date and normalize it
      const startDate = new Date(formData.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      // Update end date based on start date and duration
      const newEndDate = new Date(startDate);
      newEndDate.setDate(startDate.getDate() + (newDuration - 1));
      
      console.log(`[CampaignForm] Duration updated on blur to ${newDuration} days. New end date: ${newEndDate.toISOString()}`);
      
      setFormData(prev => ({
        ...prev,
        endDate: newEndDate
      }));
    }
  };

  // Add handlers for promotion and content days
  const handlePromotionDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPromotionDays = parseInt(e.target.value, 10) || 1;
    
    if (newPromotionDays < 1) {
      return; // Enforce minimum of 1 day
    }
    
    setPromotionDays(newPromotionDays);
    
    // Calculate total duration and update end date
    const totalDays = contentDays + newPromotionDays;
    if (totalDays > 0) {
      setDuration(totalDays);
      
      // Update end date based on start date and total duration
      const newEndDate = new Date(formData.startDate);
      newEndDate.setDate(newEndDate.getDate() + totalDays - 1);
      
      // Update form data with new end date and rcf_data
      setFormData(prev => ({ 
        ...prev, 
        endDate: newEndDate,
        rcf_data: {
          ...prev.rcf_data,
          contentDays: contentDays,
          promotionDays: newPromotionDays
        }
      }));
    }
  };
  
  const handleContentDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newContentDays = parseInt(e.target.value, 10) || 0;
    
    // Allow 0 content days for RCF planner campaigns
    if (newContentDays < 0) {
      return; // Only enforce non-negative
    }
    
    setContentDays(newContentDays);
    
    // Calculate total duration and update end date
    const totalDays = newContentDays + promotionDays;
    if (totalDays > 0) {
      setDuration(totalDays);
      
      // Update end date based on start date and total duration
      const newEndDate = new Date(formData.startDate);
      newEndDate.setDate(newEndDate.getDate() + totalDays - 1);
      
      // Update form data with new end date and rcf_data
      setFormData(prev => ({ 
        ...prev, 
        endDate: newEndDate,
        rcf_data: {
          ...prev.rcf_data,
          contentDays: newContentDays,
          promotionDays: promotionDays
        }
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Implementation of handleInputChange
  };

  // Handle the form clicks to stop propagation
  const handleFormClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // New handler for the Add Link button click
  const handleAddLinkButtonClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Good practice for button clicks triggering actions
    e.stopPropagation(); // Prevent event from bubbling up

    console.log("Add Link button clicked", { linkUrl, linkText });

    if (!linkUrl) {
      setLinkError("URL is required");
      return;
    }

    setLinkError(null);
    try {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      const activeEditor = isNotesExpanded ? expandedEditor : inlineEditor;

      if (activeEditor) {
        console.log("Active editor found", { isEditable: activeEditor.isEditable });

        // Focus the editor first
        activeEditor.commands.focus();

        // Insert the link with proper HTML
        if (linkText) {
          console.log("Inserting link with custom text:", linkText);
          activeEditor.commands.insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`);
        } else {
          console.log("Inserting link with URL as text:", url);
          activeEditor.commands.insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
        }

        // Clear form and close the *link input form only*
        setLinkUrl('');
        setLinkText('');
        setShowLinkInput(false);
      } else {
        console.error("No active editor found");
      }
    } catch (err) {
      console.error("Error inserting link:", err);
    }
  };

  // Add save handler
  const handleSaveNotes = () => {
    // Sanitize notes before saving
    const sanitizedNotes = sanitizeHtml(formData.notes);
    
    setSavedNotes(sanitizedNotes);
    setFormData(prev => ({ ...prev, notes: sanitizedNotes }));
    setIsEditing(false);
    
    // Update both editors to reflect saved state
    inlineEditor?.commands.setContent(sanitizedNotes);
    expandedEditor?.commands.setContent(sanitizedNotes);
  };

  // Add edit handler
  const handleEditNotes = () => {
    setIsEditing(true);
    // Ensure the active editor is focused when entering edit mode
    const activeEditor = isNotesExpanded ? expandedEditor : inlineEditor;
    if (activeEditor) {
      activeEditor.commands.focus();
      // Force the editor to be editable
      activeEditor.setEditable(true);
    }
  };

  // Fix the date input handler to properly handle dates without timezone issues
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Parse the date string from the input (YYYY-MM-DD format)
    const dateStr = e.target.value;
    
    // Create a date object that preserves the local date exactly as entered
    // Add the time part as a string to ensure the date is interpreted in the specified timezone
    const date = new Date(dateStr + 'T00:00:00');
    
    console.log('[TIME DEBUG] Date from input:', {
      inputValue: dateStr,
      dateObject: date,
      formattedForDisplay: formatDateForInput(date),
      timeZoneOffset: date.getTimezoneOffset(),
      selectedTimezone: timezone
    });
    
    // Update form data with the new date
    setFormData(prev => ({ ...prev, startDate: date }));
  };

  const formatDateForInput = (date: Date) => {
    // Format date as YYYY-MM-DD for input fields, preserving local date
    // This ensures the date picker shows the correct date in the user's selected timezone
    try {
      // Use the date directly since we're just formatting for display
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  // Function to display timezone info for the user
  const getTimezoneDisplay = () => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      
      // Extract the timezone abbreviation (e.g., EDT, PDT)
      const parts = formatter.formatToParts(now);
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      const tzAbbr = tzPart ? tzPart.value : '';
      
      // Get a human-readable name for the timezone
      let tzName = timezone;
      
      // Common timezone mappings
      const tzMap: Record<string, string> = {
        'America/New_York': 'Eastern Time',
        'America/Chicago': 'Central Time',
        'America/Denver': 'Mountain Time',
        'America/Los_Angeles': 'Pacific Time',
        'America/Phoenix': 'Arizona Time',
        'Europe/London': 'UK Time',
        'Europe/Paris': 'Central European Time',
        'Asia/Tokyo': 'Japan Time',
        'Australia/Sydney': 'Australian Eastern Time',
        'UTC': 'Coordinated Universal Time'
      };
      
      if (tzMap[timezone]) {
        tzName = tzMap[timezone];
      } else {
        // Try to make the raw timezone more readable
        tzName = timezone.split('/').pop()?.replace('_', ' ') || timezone;
      }
      
      return `${tzName} (${tzAbbr})`;
    } catch (error) {
      console.error('Error getting timezone display:', error);
      return timezone;
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          {/* Modal Content - Conditionally hide if notes are expanded */}  
          <div className={`relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg ${isNotesExpanded ? 'hidden' : ''}`}>
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <h3 className="text-2xl font-semibold leading-6 text-gray-900 mb-6">
                {campaign ? (markedForDeletion ? 'Confirm Delete Campaign' : 'Edit Campaign') : 'Create Campaign'}
              </h3>
              {/* Regular Form - Rendered inside the modal */} 
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                {markedForDeletion ? (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <p className="text-sm text-red-700">
                          Are you sure you want to delete this campaign? Click "Confirm Delete" to confirm deletion or "Cancel" to keep it.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Campaign Name, Type, Dates, Duration... */}                   
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Campaign Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                        Campaign Type
                      </label>
                      <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'promotion' | 'content' | 'rcf_planner' }))}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900"
                        required
                      >
                        <option value="promotion">Promotion</option>
                        <option value="content">Content</option>
                        <option value="rcf_planner">RCF Planner</option>
                      </select>
                    </div>
                    <div>
                      <div className="flex justify-between items-center">
                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                          Start Date
                        </label>
                        <span className="text-xs text-gray-500">
                          Timezone: {getTimezoneDisplay()}
                        </span>
                      </div>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        value={formatDateForInput(formData.startDate)}
                        onChange={handleDateChange}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900 [color-scheme:light]"
                        required
                      />
                    </div>
                    {formData.type !== 'rcf_planner' ? (
                      <div>
                        <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                          Duration (days)
                        </label>
                        <input
                          type="number"
                          id="duration"
                          name="duration"
                          min="1"
                          value={duration}
                          onChange={handleDurationChange}
                          onBlur={handleDurationBlur}
                          className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-[#1E3B8C] focus:ring-[#1E3B8C] text-gray-900 spinner-always-visible"
                          onClick={handleSpinnerClick}
                          data-readonly="true"
                          style={{
                            WebkitAppearance: 'textfield', /* Ensure spinner buttons appear */
                            MozAppearance: 'textfield'
                          }}
                        />
                        <p className="mt-1 text-xs text-gray-500">Use the up/down arrows to adjust</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label htmlFor="contentDays" className="block text-sm font-medium text-orange-700">
                            Content Days <span className="text-orange-500">*</span>
                          </label>
                          <input
                            type="number"
                            id="contentDays"
                            name="contentDays"
                            min="0"
                            value={contentDays}
                            onChange={handleContentDaysChange}
                            className="mt-1 block w-full rounded-lg border-orange-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-gray-900 spinner-always-visible"
                            onClick={handleSpinnerClick}
                            data-readonly="true"
                            style={{
                              WebkitAppearance: 'textfield', /* Ensure spinner buttons appear */
                              MozAppearance: 'textfield'
                            }}
                          />
                          <p className="mt-1 text-xs text-gray-500">Cannot be blank. Use the up/down arrows to adjust.</p>
                        </div>
                        <div>
                          <label htmlFor="promotionDays" className="block text-sm font-medium text-green-700">
                            Promotion Days <span className="text-green-500">*</span>
                          </label>
                          <input
                            type="number"
                            id="promotionDays"
                            name="promotionDays"
                            min="1"
                            value={promotionDays}
                            onChange={handlePromotionDaysChange}
                            className="mt-1 block w-full rounded-lg border-green-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-gray-900 spinner-always-visible"
                            onClick={handleSpinnerClick}
                            data-readonly="true"
                            style={{
                              WebkitAppearance: 'textfield', /* Ensure spinner buttons appear */
                              MozAppearance: 'textfield'
                            }}
                          />
                          <p className="mt-1 text-xs text-gray-500">Minimum 1 day required for promotion. Use the up/down arrows to adjust.</p>
                        </div>
                        <div className="pt-3 mt-2 text-xs text-gray-500">
                          Total Campaign Duration: {promotionDays + contentDays} days
                        </div>
                      </div>
                    )}

                    {/* Notes Section (Inline) */} 
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                          Campaign Notes
                        </label>
                        <div className="flex gap-2">
                          {!isEditing ? (
                            <button
                              type="button"
                              onClick={handleEditNotes}
                              className="text-xs text-white bg-[#1E3B8C] hover:bg-[#1E3B8C]/90 active:bg-[#1E3B8C]/80 rounded-lg px-2 py-1 flex items-center gap-1 shadow-sm"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit Notes
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={handleSaveNotes}
                              className="text-xs text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-lg px-2 py-1 flex items-center gap-1 shadow-sm"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Save Notes
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (inlineEditor && expandedEditor) {
                                expandedEditor.commands.setContent(inlineEditor.getHTML());
                              }
                              setIsNotesExpanded(true);
                              setTimeout(() => {
                                expandedEditor?.commands.focus();
                              }, 100);
                            }}
                            className="text-xs text-white bg-[#1E3B8C] hover:bg-[#1E3B8C]/90 active:bg-[#1E3B8C]/80 rounded-lg px-2 py-1 flex items-center gap-1 shadow-sm"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                            Expand Editor
                          </button>
                        </div>
                      </div>
                      <div className={`editor-container ${isEditing ? 'edit-mode' : 'view-mode'}`}>
                        <div className="toolbar">
                          <button 
                            type="button" 
                            onClick={() => inlineEditor?.chain().focus().toggleBold().run()}
                            className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${inlineEditor?.isActive('bold') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                            title="Bold"
                            disabled={!isEditing}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M8.21 13c2.106 0 3.412-1.087 3.412-2.823 0-1.306-.984-2.283-2.324-2.386v-.055a2.176 2.176 0 0 0 1.852-2.14c0-1.51-1.162-2.46-3.014-2.46H3.843V13H8.21zM5.908 4.674h1.696c.963 0 1.517.451 1.517 1.244 0 .834-.629 1.32-1.73 1.32H5.908V4.673zm0 6.788V8.598h1.73c1.217 0 1.88.492 1.88 1.415 0 .943-.643 1.449-1.832 1.449H5.907z"/>
                            </svg>
                          </button>
                          <button 
                            type="button" 
                            onClick={() => inlineEditor?.chain().focus().toggleItalic().run()}
                            className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${inlineEditor?.isActive('italic') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                            title="Italic"
                            disabled={!isEditing}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M7.991 11.674 9.53 4.455c.123-.595.246-.71 1.347-.807l.11-.52H7.211l-.11.52c1.06.096 1.128.212 1.005.807L6.57 11.674c-.123.595-.246.71-1.346.806l-.11.52h3.774l.11-.52c-1.06-.095-1.129-.211-1.006-.806z"/>
                            </svg>
                          </button>
                          <button 
                            type="button"
                            onClick={() => inlineEditor?.chain().focus().toggleUnderline().run()}
                            className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${inlineEditor?.isActive('underline') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                            title="Underline"
                            disabled={!isEditing}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M5.313 3.136h-1.23V9.54c0 2.105 1.47 3.623 3.917 3.623s3.917-1.518 3.917-3.623V3.136h-1.23v6.323c0 1.49-.978 2.57-2.687 2.57-1.709 0-2.687-1.08-2.687-2.57V3.136zM12.5 15h-9v-1h9v1z"/>
                            </svg>
                          </button>
                          <button 
                            type="button" 
                            onClick={() => inlineEditor?.chain().focus().toggleBulletList().run()}
                            className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${inlineEditor?.isActive('bulletList') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                            title="Bullet List"
                            disabled={!isEditing}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                              <path fillRule="evenodd" d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm-3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                            </svg>
                          </button>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => {
                                inlineEditor?.chain().focus();
                                setShowLinkInput(prev => !prev);
                              }}
                              className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${showLinkInput ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                              title="Insert Link"
                              disabled={!isEditing}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
                                <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/>
                              </svg>
                            </button>
                            {/* Custom Link Input Form */} 
                            {showLinkInput && (
                              <form 
                                className="link-input-form absolute left-0 top-full mt-1 w-64"
                                onClick={handleFormClick}
                              >
                                <div className="mb-1">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">URL</label>
                                  {linkError && (
                                    <div className="text-xs text-red-500 mb-1">{linkError}</div>
                                  )}
                                  <input 
                                    type="text" 
                                    value={linkUrl} 
                                    onChange={(e) => {setLinkUrl(e.target.value); if(linkError) setLinkError(null);}}
                                    className={`w-full p-1 border ${linkError ? 'border-red-500' : 'border-gray-300'} rounded text-sm`}
                                    placeholder="https://example.com"
                                    onClick={handleFormClick}
                                  />
                                </div>
                                <div className="mb-2">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Display Text (optional)</label>
                                  <input 
                                    type="text" 
                                    value={linkText} 
                                    onChange={(e) => setLinkText(e.target.value)}
                                    className="w-full p-1 border border-gray-300 rounded text-sm"
                                    placeholder="Link text"
                                    onClick={handleFormClick}
                                  />
                                </div>
                                <div className="flex justify-between">
                                  <button 
                                    type="button" 
                                    onClick={() => {
                                      setShowLinkInput(false);
                                    }}
                                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded border border-gray-300"
                                  >
                                    Cancel
                                  </button>
                                  <button 
                                    type="button"
                                    className="px-2 py-1 text-xs bg-[#1E3B8C] hover:bg-[#1E3B8C]/90 text-white rounded"
                                    onClick={handleAddLinkButtonClick}
                                  >
                                    Add Link
                                  </button>
                                </div>
                              </form>
                            )}
                          </div>
                        </div>
                        <div className="editor-content" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          <EditorContent editor={inlineEditor} className="tiptap-editor prose prose-sm max-w-none focus:outline-none" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Error Message */} 
                    {error && (
                      <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                          <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                 {/* Form Actions (Submit, Cancel, Delete) */} 
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 mt-6">
                  {/* ... Action Buttons ... */}                  
                   <button
                    type="submit"
                    className="inline-flex w-full justify-center rounded-lg bg-[#1E3B8C] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1E3B8C]/90 active:bg-[#1E3B8C]/80 sm:ml-3 sm:w-auto focus:outline-none focus:ring-0"
                  >
                    {markedForDeletion ? 'Confirm Delete' : (campaign ? 'Update Campaign' : 'Create Campaign')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMarkedForDeletion(false);
                      onClose();
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100 sm:mt-0 sm:w-auto focus:outline-none focus:ring-0"
                  >
                    Cancel
                  </button>
                  {campaign && onDelete && !markedForDeletion && (
                    <button
                      type="button"
                      onClick={() => {
                        console.log('Setting markedForDeletion to true for campaign:', campaign.id);
                        setMarkedForDeletion(true);
                      }}
                      className="mt-3 inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 active:bg-red-700 sm:mt-0 sm:w-auto focus:outline-none focus:ring-0 mr-auto"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
          
          {/* Expanded Notes Editor - Rendered outside the main modal div */} 
          {isNotesExpanded && (
            <div className="fixed inset-0 bg-white z-50 flex flex-col p-4 sm:p-6 expanded-editor" style={{ height: 'calc(100vh - 4rem)', top: '2rem', left: '25vw', right: '25vw', bottom: '2rem', borderRadius: '0.75rem', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)' }}> 
              {/* Header */} 
              <div className="flex items-center justify-between pb-4 border-b mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Campaign Notes Editor</h3>
                <div className="flex gap-2">
                  {!isEditing ? (
                    <button
                      type="button"
                      onClick={handleEditNotes}
                      className="text-xs text-white bg-[#1E3B8C] hover:bg-[#1E3B8C]/90 active:bg-[#1E3B8C]/80 rounded-lg px-2 py-1 flex items-center gap-1 shadow-sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Notes
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSaveNotes}
                      className="text-xs text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-lg px-2 py-1 flex items-center gap-1 shadow-sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Notes
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (inlineEditor && expandedEditor) {
                        inlineEditor.commands.setContent(expandedEditor.getHTML());
                      }
                      setIsNotesExpanded(false);
                      setTimeout(() => {
                        inlineEditor?.commands.focus();
                      }, 100);
                    }}
                    className="text-xs text-white bg-[#1E3B8C] hover:bg-[#1E3B8C]/90 active:bg-[#1E3B8C]/80 rounded-lg px-3 py-1.5 flex items-center gap-1.5 shadow-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Form
                  </button>
                </div>
              </div>
              {/* Toolbar and Editor Area */} 
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className={`editor-container ${isEditing ? 'edit-mode' : 'view-mode'}`}>
                  <div className="toolbar">
                    <button 
                      type="button" 
                      onClick={() => expandedEditor?.chain().focus().toggleBold().run()}
                      className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${expandedEditor?.isActive('bold') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                      title="Bold"
                      disabled={!isEditing}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8.21 13c2.106 0 3.412-1.087 3.412-2.823 0-1.306-.984-2.283-2.324-2.386v-.055a2.176 2.176 0 0 0 1.852-2.14c0-1.51-1.162-2.46-3.014-2.46H3.843V13H8.21zM5.908 4.674h1.696c.963 0 1.517.451 1.517 1.244 0 .834-.629 1.32-1.73 1.32H5.908V4.673zm0 6.788V8.598h1.73c1.217 0 1.88.492 1.88 1.415 0 .943-.643 1.449-1.832 1.449H5.907z"/>
                      </svg>
                    </button>
                    <button 
                      type="button" 
                      onClick={() => expandedEditor?.chain().focus().toggleItalic().run()}
                      className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${expandedEditor?.isActive('italic') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                      title="Italic"
                      disabled={!isEditing}
                    >
                       <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M7.991 11.674 9.53 4.455c.123-.595.246-.71 1.347-.807l.11-.52H7.211l-.11.52c1.06.096 1.128.212 1.005.807L6.57 11.674c-.123.595-.246.71-1.346.806l-.11.52h3.774l.11-.52c-1.06-.095-1.129-.211-1.006-.806z"/>
                      </svg>
                    </button>
                    <button 
                      type="button"
                      onClick={() => expandedEditor?.chain().focus().toggleUnderline().run()}
                      className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${expandedEditor?.isActive('underline') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                      title="Underline"
                      disabled={!isEditing}
                    >
                       <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M5.313 3.136h-1.23V9.54c0 2.105 1.47 3.623 3.917 3.623s3.917-1.518 3.917-3.623V3.136h-1.23v6.323c0 1.49-.978 2.57-2.687 2.57-1.709 0-2.687-1.08-2.687-2.57V3.136zM12.5 15h-9v-1h9v1z"/>
                      </svg>
                    </button>
                    <button 
                      type="button" 
                      onClick={() => expandedEditor?.chain().focus().toggleBulletList().run()}
                      className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${expandedEditor?.isActive('bulletList') ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                      title="Bullet List"
                      disabled={!isEditing}
                    >
                       <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm-3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                      </svg>
                    </button>
                    <div className="relative">
                      <button 
                        type="button" 
                        onClick={() => {
                          expandedEditor?.chain().focus();
                          setShowLinkInput(prev => !prev);
                        }}
                        className={`p-1.5 rounded shadow-sm border flex items-center justify-center ${showLinkInput ? 'bg-gray-300 border-gray-400' : 'bg-white hover:bg-gray-200 border-gray-200 text-gray-700'}`}
                        title="Insert Link"
                        disabled={!isEditing}
                      >
                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
                          <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/>
                        </svg>
                      </button>
                      {/* Custom Link Input Form */} 
                       {showLinkInput && (
                         <form 
                          className="link-input-form absolute left-0 top-full mt-1 w-64"
                          onClick={handleFormClick}
                        >
                          <div className="mb-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">URL</label>
                            {linkError && (
                              <div className="text-xs text-red-500 mb-1">{linkError}</div>
                            )}
                            <input 
                              type="text" 
                              value={linkUrl} 
                              onChange={(e) => {setLinkUrl(e.target.value); if(linkError) setLinkError(null);}}
                              className={`w-full p-1 border ${linkError ? 'border-red-500' : 'border-gray-300'} rounded text-sm`}
                              placeholder="https://example.com"
                              onClick={handleFormClick}
                            />
                          </div>
                          <div className="mb-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Display Text (optional)</label>
                            <input 
                              type="text" 
                              value={linkText} 
                              onChange={(e) => setLinkText(e.target.value)}
                              className="w-full p-1 border border-gray-300 rounded text-sm"
                              placeholder="Link text"
                              onClick={handleFormClick}
                            />
                          </div>
                          <div className="flex justify-between">
                            <button 
                              type="button" 
                              onClick={() => {
                                setShowLinkInput(false);
                              }}
                              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded border border-gray-300"
                            >
                              Cancel
                            </button>
                            <button 
                              type="button"
                              className="px-2 py-1 text-xs bg-[#1E3B8C] hover:bg-[#1E3B8C]/90 text-white rounded"
                              onClick={handleAddLinkButtonClick}
                            >
                              Add Link
                            </button>
                          </div>
                        </form>
                       )}
                     </div>
                  </div>
                  <div className="editor-content">
                    <EditorContent editor={expandedEditor} className="tiptap-editor prose prose-sm max-w-none focus:outline-none min-h-full" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}; 